import { IMedia } from '@dtos/Course'
import { View, XStack, Text, isWeb, Spinner } from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { Button } from '@components/Button'
import { useTranslation } from 'react-i18next'
import { useContentDownload } from '@hooks/useContentDownload'

interface INotSupportedProps {
  media: IMedia
  fileUrl?: string
}

export function NotSupported({ media, fileUrl }: INotSupportedProps) {
  const { t } = useTranslation('ExtraContent')
  const { download: handlePressDownload, isDownloading } = useContentDownload({
    fileExt: media.fileExt,
  })

  const isVideo = media?.type === 'Video'
  const setColor = isVideo ? '$text-disabled' : '$accent'

  if (isWeb) {
    return (
      <View
        width="100%"
        height="100%"
        flex={1}
        alignItems="center"
        justifyContent="center"
        gap="$spacing-inset-xs"
        paddingVertical={50}
        $gtSm={{
          paddingHorizontal: 40,
        }}
      >
        <HeroIcon icon={['fal', 'download']} size={40} color="$icon-primary" />
        <Text
          color="$text-primary"
          ff="$mulishRegular"
          fontSize="$font-size-xs"
          textAlign="center"
          whiteSpace="pre-line"
        >
          {t('NotSupported.title')}
        </Text>
        {media && (
          <Button
            variant="primary"
            testID="not-supported-download-button"
            outline
            onPress={() => fileUrl && handlePressDownload(fileUrl, true)}
            disabled={isVideo || isDownloading}
          >
            <XStack gap="$spacing-inset-nano" alignItems="center">
              {isDownloading ? (
                <Spinner size="small" color={setColor} />
              ) : (
                <HeroIcon
                  size={20}
                  icon={['fal', 'download']}
                  color={setColor}
                />
              )}
              <Text
                fontSize="$font-size-xxs"
                ff="$mulishBold"
                color={setColor}
                position="relative"
                testID="not-supported-button-text"
              >
                {isDownloading
                  ? t('NotSupported.downloading')
                  : t('NotSupported.download')}
              </Text>
            </XStack>
          </Button>
        )}
      </View>
    )
  }

  return (
    // Card no padrão do Figma (node 9071-63076): thumb 16:9 arredondada com
    // fundo neutro, ícone de arquivo esmaecido atrás de um overlay escuro e o
    // botão de download centralizado por cima.
    <View
      width="100%"
      aspectRatio={16 / 9}
      borderRadius="$border-radius-md"
      overflow="hidden"
      backgroundColor="$bg-surface-strong"
      alignItems="center"
      justifyContent="center"
    >
      <View
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        alignItems="center"
        justifyContent="center"
      >
        <HeroIcon icon={['fal', 'file']} size={120} color="$accent" />
      </View>
      <View
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        backgroundColor="rgba(0, 0, 0, 0.65)"
      />
      <Button
        variant="primary"
        testID="not-supported-download-button"
        justifyContent="center"
        onPress={() => fileUrl && handlePressDownload(fileUrl, false)}
        disabled={isDownloading || isVideo}
      >
        <XStack gap={8} alignItems="center" justifyContent="center">
          {isDownloading ? (
            <Spinner
              size="small"
              color={isDownloading ? '$text-disabled' : '$text-on-accent'}
            />
          ) : (
            <HeroIcon
              icon={['fas', 'download']}
              size={20}
              color="$text-on-accent"
            />
          )}
          <Text
            fontSize="$font-size-xs"
            ff="$mulishBold"
            fontWeight={700}
            color={isDownloading ? '$text-disabled' : '$text-on-accent'}
            mt="$spacing-inset-quarck"
            testID="not-supported-button-text"
          >
            {isDownloading ? 'Baixando conteúdo' : 'Baixar conteúdo'}
          </Text>
        </XStack>
      </Button>
    </View>
  )
}
